import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { reelUrl } from "@/lib/config";
import { useIntake, type Live } from "@/lib/intake";
import { useReducedMotion, useViewport } from "@/lib/motion";
import { Galaxy } from "./Galaxy";
import { BriefCard, GoldButton, Kicker, QuestionCard, QuietButton, ReadyCard, Sheet } from "./IntakeCards";
import { StoreBadges } from "./StoreBadges";
import { Warp } from "./Warp";

/**
 * The hero is the product's first step: Jake's galaxy new-topic screen, running the real adaptive intake
 * (POST /intake, long-poll /session/:id, /reply, /brief), then handing the session to the web app.
 */
const EXAMPLES = ["How do robots learn to grab things?", "I'm training as a counsellor", "Build a computer out of mushrooms"];

const SPARKLE = (
  <svg viewBox="0 0 24 24" className="size-[22px] shrink-0" aria-hidden>
    <path d="M12 2l2.2 6.3L20.5 10l-6.3 2.2L12 18.5l-2.2-6.3L3.5 10l6.3-1.7z" fill="#FFD36E" />
    <path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9z" fill="#C9A8FF" />
  </svg>
);

type View = "input" | "charting" | "note" | "question" | "brief" | "ready" | "error" | "intent-error";

const short = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Real progress as charting lines (port of useChartingLines in topic_flow.tsx). */
function useChartingLines(q: string | null, live: Live | null) {
  const [lines, setLines] = useState<string[]>([]);
  const add = (line: string) => setLines(ls => (ls.includes(line) ? ls : [...ls, line]));
  useEffect(() => setLines(q ? [`Reading “${short(q, 28)}”`] : []), [q]);
  const i = live?.intake;
  const it = live?.intent;
  useEffect(() => {
    if (i?.title) add(`Found your angle: ${i.title}`);
  }, [i?.title]);
  useEffect(() => {
    if (!it) return;
    if (it.status === "thinking") add(it.asked ? `Thinking about answer ${it.asked}` : "Working out where you’re headed");
    if (it.status === "brief") add("Here’s what I heard");
    if (it.status === "confirmed") add("Planning your first lesson");
  }, [it?.status, it?.asked, it?.rev]);
  const log = live?.research.log;
  useEffect(() => {
    if (log) add(log.replace(/…$/, ""));
  }, [log]);
  useEffect(() => {
    if (live?.reel.beats.length) add("First lesson ready");
  }, [live?.reel.beats.length]);
  return [lines, add] as const;
}

export function Hero() {
  const intake = useIntake();
  const { q, live, error, busy } = intake;
  const it = live?.intent;
  const [lines, addLine] = useChartingLines(q, live);
  const status = lines.at(-1);
  const reduced = useReducedMotion();
  const vp = useViewport();

  const [text, setText] = useState("");
  const [answered, setAnswered] = useState<string | null>(null);
  const [briefSent, setBriefSent] = useState(-1);
  const [noteSeen, setNoteSeen] = useState(false);
  const [warp, setWarp] = useState<{ x: number; y: number } | null>(null);

  const view: View = !q
    ? "input"
    : error
      ? "error"
      : it?.status === "error"
        ? "intent-error"
        : live?.intake.note && !noteSeen && (it?.status === "asking" || it?.status === "brief")
          ? "note"
          : it?.status === "asking" && it.question && it.question.id !== answered
            ? "question"
            : it?.status === "brief" && it.brief && it.rev !== briefSent
              ? "brief"
              : it?.status === "confirmed"
                ? "ready"
                : "charting";

  // Coming back from the app with the back button: drop the warp flash.
  useEffect(() => {
    const on = (e: PageTransitionEvent) => e.persisted && setWarp(null);
    addEventListener("pageshow", on);
    return () => removeEventListener("pageshow", on);
  }, []);

  // ---- Layout: the galaxy fits the room left above the content and glides when that changes.
  const room = useRef<HTMLDivElement>(null);
  const galaxyBox = useRef<HTMLDivElement>(null);
  const [roomBox, setRoomBox] = useState({ top: 64, h: 400 });
  useLayoutEffect(() => {
    const el = room.current;
    if (!el) return;
    const measure = () => setRoomBox({ top: el.offsetTop, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // The disc's size comes from the room the idle layout leaves (remembered while the cards are up); phases only scale it.
  const [baseRoom, setBaseRoom] = useState<number | null>(null);
  useEffect(() => {
    if (view === "input") setBaseRoom(roomBox.h);
  }, [view, roomBox.h]);
  const room0 = baseRoom ?? roomBox.h;
  const size = Math.round(Math.max(240, Math.min(780, vp.w * (vp.w < 640 ? 1.02 : 0.8), room0 / 0.66)));
  // No glide on first paint: transitions start once the room has been measured.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (baseRoom == null) return;
    const r = requestAnimationFrame(() => requestAnimationFrame(() => setSettled(true)));
    return () => cancelAnimationFrame(r);
  }, [baseRoom != null]);
  const fit = (roomBox.h * 0.94) / (0.65 * size);
  const modeScale = { input: 1, charting: 1.05, note: 1, question: 1, brief: 1, ready: 1.06, error: 0.9, "intent-error": 0.9 }[view];
  const k = warp ? 7 : Math.max(0.28, Math.min(modeScale, fit));
  const speed = warp ? 0.05 : { input: 1, charting: 0.18, note: 0.6, question: 0.6, brief: 0.45, ready: 0.3, error: 1.6, "intent-error": 1.6 }[view];
  const glow = { input: 0, charting: 1, note: 0.4, question: 0.45, brief: 0.8, ready: 1, error: 0, "intent-error": 0 }[view];
  // One pulse from the core per step forward.
  const pulse = q ? lines.length + (view === "question" ? 100 : 0) + (view === "brief" ? 200 : 0) + (view === "ready" ? 300 : 0) : 0;

  const begin = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    setAnswered(null);
    setBriefSent(-1);
    setNoteSeen(false);
    intake.start(t);
  };
  const changeTopic = () => {
    intake.reset();
    setTimeout(() => document.getElementById("learn-input")?.focus(), 50);
  };
  const go = () => {
    if (!live) return;
    const url = reelUrl(live.id);
    if (reduced) {
      location.assign(url);
      return;
    }
    const r = galaxyBox.current?.getBoundingClientRect();
    setWarp({ x: r ? r.left + r.width / 2 : innerWidth / 2, y: r ? r.top + r.height / 2 : innerHeight / 3 });
    setTimeout(() => location.assign(url), 1250);
  };

  const topic = q ? q[0]!.toUpperCase() + q.slice(1) : "";
  const shown = lines.slice(-3);

  return (
    <section id="top" className="relative flex min-h-[100svh] flex-col overflow-hidden pt-16" aria-label="Start learning">
      {/* The screen's backdrop: a radial glow a third of the way down. */}
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse 85% 70% at 50% 34%, #0C1452 0%, rgba(7,12,60,0.85) 45%, rgba(4,7,42,0) 100%)" }} />

      {/* The galaxy, centred in whatever room the content leaves. */}
      <div
        ref={galaxyBox}
        className="pointer-events-none absolute left-1/2"
        style={{
          top: roomBox.top + roomBox.h / 2,
          width: size,
          height: size,
          marginLeft: -size / 2,
          marginTop: -size / 2,
          transform: `translateY(${view === "charting" ? size * 0.02 : 0}px) scale(${k})`,
          opacity: warp ? 0 : 1,
          transition: warp
            ? "transform 1250ms cubic-bezier(.55,0,.9,.4), opacity 500ms 750ms"
            : settled
              ? "transform 750ms cubic-bezier(.3,1,.4,1), top 750ms cubic-bezier(.3,1,.4,1), opacity 400ms"
              : "none",
          visibility: baseRoom == null ? "hidden" : undefined,
        }}
      >
        <Galaxy size={size} speed={speed} glow={glow} pulse={pulse} />
      </div>

      <div ref={room} className="min-h-[120px] flex-1" aria-hidden />

      <div className="relative z-10 mx-auto w-full max-w-[520px] px-4 md:max-w-[620px] pb-6 sm:px-5 sm:pb-10">
        {view === "input" && (
          <div className="la-pop-in">
            <p className="text-center text-sm font-extrabold text-[rgba(220,215,255,0.72)] sm:text-[15px]">Personal courses for wherever you’re headed.</p>
            <h1 className="mt-2 text-center text-[2.1rem] leading-[1.1] font-black tracking-tight text-white [text-shadow:0_2px_24px_rgba(107,78,255,0.6)] sm:text-5xl">
              What do you want to learn?
            </h1>
            <form
              className="mt-5"
              onSubmit={e => {
                e.preventDefault();
                begin(text);
              }}
            >
              <div className="group relative flex h-[60px] items-center gap-3 rounded-[20px] border-2 border-[rgba(160,140,255,0.45)] bg-white/[0.07] pr-1.5 pl-3.5 transition focus-within:border-[#9B82FF] focus-within:shadow-[0_0_0_5px_rgba(107,78,255,0.25),0_0_36px_rgba(107,78,255,0.35)]">
                {SPARKLE}
                <label htmlFor="learn-input" className="sr-only">
                  What do you want to learn?
                </label>
                <input
                  id="learn-input"
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder="A skill, a goal, a question…"
                  autoComplete="off"
                  enterKeyHint="go"
                  maxLength={4000}
                  className="h-14 min-w-0 flex-1 bg-transparent text-base font-extrabold text-white caret-[#9B82FF] placeholder:font-bold placeholder:text-[rgba(220,215,255,0.45)] focus:outline-none sm:text-[17px]"
                />
                <button
                  type="submit"
                  disabled={!text.trim()}
                  className="hidden h-11 shrink-0 rounded-[14px] border-b-4 border-[#4128B8] bg-[#6B4EFF] px-5 text-base font-black text-white shadow-[0_0_28px_rgba(107,78,255,0.5)] transition hover:brightness-110 active:translate-y-[2px] active:border-b-2 disabled:border-transparent disabled:bg-white/10 disabled:text-white/40 disabled:shadow-none sm:block"
                >
                  Start
                </button>
              </div>
              <div className="mt-3.5 flex flex-wrap justify-center gap-2">
                {EXAMPLES.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      setText(c);
                      document.getElementById("learn-input")?.focus();
                    }}
                    className="rounded-full border-[1.5px] border-[rgba(160,140,255,0.35)] bg-white/[0.05] px-3.5 py-2 text-[13px] font-extrabold text-[#DCD6FF] transition hover:border-[rgba(160,140,255,0.7)] hover:bg-white/[0.09] sm:text-sm"
                  >
                    {c}
                  </button>
                ))}
              </div>
              <button
                type="submit"
                disabled={!text.trim()}
                className="mt-4 h-14 w-full rounded-[18px] border-b-[5px] border-[#4128B8] bg-[#6B4EFF] text-[17px] font-black text-white shadow-[0_0_36px_rgba(107,78,255,0.45)] transition active:translate-y-[3px] active:border-b-2 disabled:border-transparent disabled:bg-white/[0.08] disabled:text-white/40 disabled:shadow-none sm:hidden"
              >
                Start
              </button>
            </form>
            <p className="mt-4 text-center text-[13px] leading-snug font-bold text-[rgba(220,215,255,0.55)]">Say it in one sentence. Your first lesson is ready in about a minute.</p>
            <div className="mt-5 flex flex-col items-center gap-2 border-t border-white/[0.08] pt-4">
              <p className="text-xs font-extrabold text-[rgba(220,215,255,0.5)]">or get the app</p>
              <StoreBadges compact center />
            </div>
          </div>
        )}

        {view === "charting" && (
          <div className="la-pop-in flex min-h-[150px] flex-col items-center pb-3 text-center" aria-live="polite">
            <Kicker>Charting your galaxy</Kicker>
            <p className="mt-2.5 line-clamp-2 text-[26px] leading-[1.15] font-black text-white">{topic}</p>
            {shown.map((line, i) => {
              const now = i === shown.length - 1;
              return (
                <p key={line} className={`la-fade-up mt-2.5 max-w-full truncate text-[15px] font-extrabold ${now ? "text-white" : "text-[rgba(220,215,255,0.6)]"}`}>
                  {now ? `${line}…` : `✓ ${line}`}
                </p>
              );
            })}
          </div>
        )}

        {view === "note" && live?.intake.note && (
          <Sheet key="note" status={status} label="A note before we start">
            <p className="text-[19px] leading-[1.3] font-black text-white">{live.intake.note}</p>
            <p className="text-sm leading-snug font-bold text-white/70">You can pause or close any time.</p>
            <GoldButton onClick={() => setNoteSeen(true)}>Okay</GoldButton>
          </Sheet>
        )}

        {view === "question" && it?.question && (
          <QuestionCard
            key={it.question.id}
            prompt={it.question.prompt}
            chips={it.question.chips.map(c => c.label)}
            n={(it.asked ?? 0) + 1}
            status={status}
            onChip={label => {
              setAnswered(it.question!.id);
              intake.reply(it.question!.id, { chip: label });
            }}
            onText={t => {
              setAnswered(it.question!.id);
              intake.reply(it.question!.id, { text: t });
            }}
            onSkip={() => {
              setAnswered(it.question!.id);
              intake.reply(it.question!.id, { skipped: true });
            }}
          />
        )}

        {view === "brief" && it?.brief && (
          <BriefCard
            key={`b${it.rev}`}
            brief={it.brief}
            status={status}
            busy={busy}
            onConfirm={() => {
              setBriefSent(it.rev);
              intake.confirm();
            }}
            onEdit={t => {
              setBriefSent(it.rev);
              addLine(it.rev > 1 ? "Updating the brief again" : "Updating the brief");
              intake.edit(t);
            }}
          />
        )}

        {view === "ready" && live && <ReadyCard live={live} status={status} onGo={go} onRestart={changeTopic} />}

        {(view === "error" || view === "intent-error") && (
          <Sheet key="err" label="Something went wrong">
            <Kicker>Hold on</Kicker>
            <h2 className="text-[19px] leading-[1.22] font-black text-white">
              {view === "intent-error" ? "I lost my train of thought" : error === "Couldn't reach the lesson server." ? "Couldn’t reach the lesson server" : "Something went wrong on our side"}
            </h2>
            <p className="text-sm leading-snug font-bold text-white/75">
              {view === "intent-error"
                ? it?.error
                : error === "Couldn't reach the lesson server."
                  ? "Lessons are written live, so nothing can start until the server is back. Check your connection and try again."
                  : error}
            </p>
            <GoldButton onClick={view === "intent-error" ? intake.restart : intake.retry}>Retry</GoldButton>
            <QuietButton onClick={changeTopic}>Change topic</QuietButton>
          </Sheet>
        )}

        {view === "input" && (
          <a href="#how" className="mx-auto mt-6 hidden w-fit items-center gap-2 text-sm font-extrabold text-[rgba(220,215,255,0.55)] hover:text-white sm:flex">
            See how it works
            <svg viewBox="0 0 16 16" className="size-4" aria-hidden>
              <path d="M8 3v10M3.5 8.5 8 13l4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        )}
      </div>

      {warp && <Warp x={warp.x} y={warp.y} onDone={() => {}} />}
    </section>
  );
}
